import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';

const postJson = (url, body) => fetch(url, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
}).then(r => r.json());

const askNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
        try { await Notification.requestPermission(); } catch {}
    }
};

// 계정 로그인(회원·관리자) / 채팅방 비밀번호 + 닉네임(게스트)
export function LoginModal({ hint, defaultTab = 'account' }) {
    const { signIn } = useAuth();
    const [tab, setTab] = useState(defaultTab);
    const [loginId, setLoginId] = useState('');
    const [password, setPassword] = useState('');
    const [roomPw, setRoomPw] = useState('');
    const [roomStep, setRoomStep] = useState('password');
    const [nickname, setNickname] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    // 로그인 상태 유지 (기본 켜짐). 끄면 브라우저를 닫을 때 로그아웃 — 학교·PC방 공용 PC 용
    const [remember, setRemember] = useState(true);
    const idRef = useRef(null), pwRef = useRef(null), roomPwRef = useRef(null), nickRef = useRef(null);

    useEffect(() => {
        if (tab === 'account') idRef.current?.focus();
        else if (roomStep === 'password') roomPwRef.current?.focus();
        else nickRef.current?.focus();
    }, [tab, roomStep]);

    const run = async (fn) => {
        if (busy) return;
        setBusy(true); setError('');
        try { await fn(); } catch { setError('서버 연결 실패'); } finally { setBusy(false); }
    };

    const handleAccountLogin = () => run(async () => {
        if (!loginId.trim() || !password) return;
        const data = await postJson('/api/auth/login', { loginId: loginId.trim(), password, remember });
        if (!data.ok) { setError(data.message || '로그인 실패'); setPassword(''); pwRef.current?.focus(); return; }
        await askNotificationPermission();
        await signIn({ ...data, remember });
    });

    const handleRoomPw = () => run(async () => {
        if (!roomPw) return;
        const data = await postJson('/api/auth/verify', { password: roomPw });
        if (!data.ok) { setError('비밀번호가 틀렸습니다.'); setRoomPw(''); return; }
        setRoomStep('nickname');
    });

    const handleNickname = () => run(async () => {
        const v = nickname.trim();
        if (!v) return;
        const data = await postJson('/api/auth/guest-login', { password: roomPw, nickname: v });
        if (!data.ok) { setError(data.message || '입장할 수 없습니다.'); return; }
        await askNotificationPermission();
        await signIn({ ...data, remember });
    });

    const switchTab = (next) => { setTab(next); setError(''); };

    return (
        <div id="passwordModal">
            <div id="passwordModalContent" style={{ maxWidth: 360, width: '100%' }}>
                <h2 style={{ marginBottom: 8 }}>Break Chat</h2>
                {hint && <p className="login-hint">{hint}</p>}
                <div className="login-tabs">
                    <button className={tab === 'account' ? 'active' : ''} onClick={() => switchTab('account')}>🔐 계정 로그인</button>
                    <button className={tab === 'password' ? 'active' : ''} onClick={() => switchTab('password')}>🔑 비밀번호 입장</button>
                </div>
                {tab === 'account' && (
                    <>
                        <input ref={idRef} type="text" id="passwordInput" placeholder="아이디" autoComplete="username" value={loginId}
                            onChange={e => { setLoginId(e.target.value); setError(''); }} onKeyDown={e => e.key === 'Enter' && pwRef.current?.focus()} style={{ marginBottom: 10 }} />
                        <input ref={pwRef} type="password" id="passwordInput" placeholder="비밀번호" autoComplete="current-password" value={password}
                            onChange={e => { setPassword(e.target.value); setError(''); }} onKeyDown={e => e.key === 'Enter' && handleAccountLogin()} style={{ marginBottom: 10 }} />
                        <button id="passwordSubmit" onClick={handleAccountLogin} disabled={busy}>{busy ? '로그인 중...' : '로그인'}</button>
                    </>
                )}
                {tab === 'password' && roomStep === 'password' && (
                    <>
                        <input ref={roomPwRef} type="password" id="passwordInput" placeholder="채팅방 비밀번호" value={roomPw}
                            onChange={e => { setRoomPw(e.target.value); setError(''); }} onKeyDown={e => e.key === 'Enter' && handleRoomPw()} />
                        <button id="passwordSubmit" onClick={handleRoomPw} disabled={busy}>확인</button>
                    </>
                )}
                {tab === 'password' && roomStep === 'nickname' && (
                    <>
                        <input ref={nickRef} type="text" id="passwordInput" placeholder="닉네임 (2~10자)" maxLength={10} value={nickname}
                            onChange={e => { setNickname(e.target.value); setError(''); }} onKeyDown={e => e.key === 'Enter' && handleNickname()} />
                        <button id="passwordSubmit" onClick={handleNickname} disabled={busy}>입장</button>
                        <button className="login-back" onClick={() => { setRoomStep('password'); setError(''); }}>← 비밀번호 다시 입력</button>
                    </>
                )}
                <label className="login-remember" title="공용 PC(학교·PC방)에서는 끄세요. 브라우저를 닫으면 로그아웃됩니다.">
                    <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
                    <span>로그인 상태 유지</span>
                    {!remember && <small>브라우저를 닫으면 로그아웃됩니다</small>}
                </label>
                <div id="passwordError">{error}</div>
            </div>
        </div>
    );
}
